/**
 * Vest NQ-PERP volume spikes ("🐋" on the candle).
 *
 * Relative volume of a chart bar = its Vest volume / a baseline, both per 2m
 * bar inside the candle (the 2m bars are first re-aggregated exactly like the
 * CME candles, utils/vestVolume.ts; a 3m/5m candle holds a varying number of
 * whole 2m bars, 1-2 / 2-3). The baseline is the SAME rule as the CME 🦈
 * (utils/relVolBaseline.ts):
 * - auto (default): mean of the eligible bars at the same Chicago time of day
 *   over the last 10 days Vest was open at that slot, once >= 5 exist; else
 *   the previous N eligible bars. So the RTH open burst is measured against
 *   earlier opens, not against the quiet pre-open hour;
 * - tod: time of day only (same 5-day minimum, no fallback: a bar with fewer
 *   is reported in `misses` instead);
 * - trailing: the previous N eligible bars only (the pre-#590 behaviour).
 * The basis used is reported per bar (tooltips, legend).
 *
 * Eligible bar = closed and outside a reopen window:
 * - closed periods (weekend lock, holidays) have no Vest bars at all, so both
 *   baselines simply walk over them (a closed day adds no time-of-day sample);
 * - like the scanner (REOPEN_WINDOW 15 min), a bar that starts within 15
 *   minutes of a reopen is neither flagged nor used as a baseline (the reopen
 *   burst is not "unusual", and it would inflate the next baseline).
 *
 * Only closed bars are flagged, and a bar's value depends only on itself and
 * EARLIER bars, so a marker never appears and then disappears (no repaint).
 *
 * The store holds ~16 days of 2m bars (baseline history, #593) and the
 * chart shows ~1 day: callers pass `fromSec` (the chart's first bar) so only
 * the chart's bars come back; the earlier ones are baseline only.
 */
import { aggregateVestBars, VestBar, VEST_BAR_MS } from './vestVolume';
import { basisDetail, clampBaseline, RelVolMap, rollingRelVols, type RelVolBaseline, type RelVolBasis } from './relVolBaseline';

export const REOPEN_WINDOW_MS = 15 * 60_000;

export interface SpikeSettings {
  /** Trailing bars in the baseline (auto falls back to it; trailing uses only it). */
  lookback: number;
  /** Flag at relVol >= threshold. */
  threshold: number;
  /** Same modes and meaning as the CME rule (utils/relVolBaseline.ts). */
  baseline: RelVolBaseline;
}

/**
 * 2.75x on the auto baseline keeps about the old whale count without the open
 * cluster. 2m MNQ RTH (08:30-15:15 CT), both venues on auto, whales per
 * session (mean / median, share in 08:30-09:00 CT):
 *   sessions 10-12..10-21 (8): 2.0x 43.9/39, 2.5x 24.8/23, 2.75x 17.4/13 (2%),
 *     3.0x 13.1/11, 3.5x 8.4/7; old trailing 2.0x: 18.3/18 (53% at the open)
 *   sessions 10-05..10-21 (13): 2.5x 28.5, 2.75x 22.2 (3%), 3.0x 17.3;
 *     old trailing 2.0x 19.9 (48%)
 * Earlier sessions are not usable: Vest volume ramped ~40x from 2026-09-21 to
 * 10-21 (before 09-21 it was ~300x thinner), so while the 10-day window still
 * holds the ramp the time-of-day baseline lags and every rel vol reads high
 * (median RTH rel vol 3-10x in 09-21..10-02, 1.0-1.9x from 10-05). Frequency
 * only, not tuned on outcome.
 */
export const DEFAULT_SPIKE_SETTINGS: SpikeSettings = { lookback: 30, threshold: 2.75, baseline: 'auto' };
/** The default before #590 (trailing baseline): a saved 2.0 from then is migrated to the new default. */
export const PRE_590_DEFAULT_THRESHOLD = 2.0;
export const SPIKE_LOOKBACK_RANGE = { min: 5, max: 500 } as const;
export const SPIKE_THRESHOLD_RANGE = { min: 1.1, max: 20 } as const;

export function clampSpikeSettings(s: Partial<SpikeSettings> | null | undefined): SpikeSettings {
  const n = Number(s?.lookback);
  const t = Number(s?.threshold);
  return {
    lookback: Number.isFinite(n)
      ? Math.round(Math.min(SPIKE_LOOKBACK_RANGE.max, Math.max(SPIKE_LOOKBACK_RANGE.min, n)))
      : DEFAULT_SPIKE_SETTINGS.lookback,
    threshold: Number.isFinite(t)
      ? Math.min(SPIKE_THRESHOLD_RANGE.max, Math.max(SPIKE_THRESHOLD_RANGE.min, t))
      : DEFAULT_SPIKE_SETTINGS.threshold,
    // Blobs saved before the baseline existed (or junk) -> 'auto'.
    baseline: clampBaseline(s?.baseline),
  };
}

export interface VestSpike {
  /** Chart bar open, seconds. */
  time: number;
  relVol: number;
  v: number;
  /** Baseline Vest volume for a candle of this size. */
  avg: number;
  basis: RelVolBasis;
  /** Bars (trailing) or sessions (time of day: days Vest was open at that slot) behind the baseline. */
  samples: number;
  buy: number | null;
  sell: number | null;
  delta: number | null;
  /** Net taker selling draws the whale under the low; otherwise over the high. */
  side: 'above' | 'below';
}

/** 2m bar opens (ms) that start within REOPEN_WINDOW_MS of a reopen. */
export function reopenBars(sorted2m: readonly VestBar[]): Set<number> {
  const out = new Set<number>();
  let reopenAt: number | null = null;
  let prev: number | null = null;
  for (const b of sorted2m) {
    // A missing 2m bar means the market was closed (open silence is a zero bar).
    if (prev === null || b.t - prev > VEST_BAR_MS) reopenAt = b.t;
    if (reopenAt !== null && b.t - reopenAt < REOPEN_WINDOW_MS) out.add(b.t);
    prev = b.t;
  }
  return out;
}

export interface VestRelVolOptions {
  /** Only chart bars at/after this time (seconds) are returned; earlier bars are baseline only. */
  fromSec?: number;
}

/**
 * Rel vol of every eligible closed chart bar (seconds -> spike-shaped record);
 * `misses` = bars forced time of day could not rate (too few sessions), see
 * relVolMisses.
 */
export function computeVestRelVols(
  bars: Iterable<VestBar>,
  timeframeMinutes: number,
  settings: SpikeSettings,
  nowMs: number,
  opts: VestRelVolOptions = {},
): Map<number, VestSpike> {
  const { lookback, baseline } = clampSpikeSettings(settings);
  const sorted = [...bars].sort((a, b) => a.t - b.t);
  const out = new RelVolMap<VestSpike>();
  const from = opts.fromSec ?? -Infinity;
  if (sorted.length === 0) return out;
  const reopen = reopenBars(sorted);
  const intervalMs = Math.max(VEST_BAR_MS, Math.round(timeframeMinutes * 60_000));
  const sub2m = timeframeMinutes * 60_000 < VEST_BAR_MS;

  // Per chart bar: eligible (no reopen bar inside, every 2m bar closed, and
  // the chart bar's time is over).
  //
  // "Over" is decided by the engine's clock first: once its last 2m bar is
  // closed (the engine sets `closed` with b + 2m <= its now) and that bar
  // reaches the chart bar's end, the chart bar is over. The browser clock is
  // only the fallback for a chart bar a closure cuts short. Judging it by
  // Date.now() alone dropped the just-closed bar whenever the browser ran a
  // few seconds behind the engine, until some later update re-checked it.
  const flags = new Map<number, { reopen: boolean; open: boolean; closedTo: number }>();
  for (const b of sorted) {
    const key = Math.floor((sub2m ? b.t : Math.floor(b.t / intervalMs) * intervalMs) / 1000);
    const f = flags.get(key) ?? { reopen: false, open: false, closedTo: 0 };
    if (reopen.has(b.t)) f.reopen = true;
    if (!b.closed) f.open = true;
    else f.closedTo = Math.max(f.closedTo, b.t + VEST_BAR_MS);
    flags.set(key, f);
  }
  const points = [...aggregateVestBars(sorted, timeframeMinutes).values()].sort((a, b) => a.time - b.time);

  const eligible: { time: number; perSlot: number; slots: number; p: (typeof points)[number] }[] = [];
  for (const p of points) {
    const f = flags.get(p.time);
    const end = p.time * 1000 + intervalMs;
    if (!f || f.reopen || f.open) continue;
    if (f.closedTo < end && end > nowMs) continue;
    // Per 2m bar: on 3m/5m charts a candle holds 1-2 / 2-3 whole 2m bars
    // (same folding as the CME candles), which would otherwise be a sawtooth.
    eligible.push({ time: p.time, perSlot: p.v / p.bars, slots: p.bars, p });
  }
  rollingRelVols(eligible, lookback, baseline, ({ p }, hit) => {
    if (p.time < from) return;
    out.set(p.time, {
      time: p.time,
      relVol: hit.relVol,
      v: p.v,
      // what this candle would hold at the baseline rate
      avg: hit.avg,
      basis: hit.basis,
      samples: hit.samples,
      buy: p.buy,
      sell: p.sell,
      delta: p.delta,
      side: p.delta !== null && p.delta < 0 ? 'below' : 'above',
    });
  }, ({ p }, miss) => {
    if (p.time >= from) out.misses.set(p.time, miss);
  });
  return out;
}

/**
 * computeVestRelVols over the store's bar map, cached on (map identity, chart
 * timeframe, settings, fromSec, second). The store hands out a new map only
 * when bars change, and the candle feed and the pane both ask after every
 * Vest update: with ~16 days of baseline history one pass is ~6-9 ms
 * (measured), so they share it.
 */
let relVolCache: { key: unknown[]; value: Map<number, VestSpike> } | null = null;
export function vestRelVolsFor(
  bars: ReadonlyMap<number, VestBar>,
  timeframeMinutes: number,
  settings: SpikeSettings,
  nowMs: number,
  opts: VestRelVolOptions = {},
): Map<number, VestSpike> {
  const s = clampSpikeSettings(settings);
  const key = [bars, timeframeMinutes, s.lookback, s.baseline, opts.fromSec, Math.floor(nowMs / 1000)];
  if (relVolCache && relVolCache.key.every((k, i) => k === key[i])) return relVolCache.value;
  const value = computeVestRelVols(bars.values(), timeframeMinutes, s, nowMs, opts);
  relVolCache = { key, value };
  return value;
}

/**
 * Spikes on the chart timeframe. `bars` = the store's 2m bars (any order);
 * `nowMs` decides which chart bars are over.
 */
export function computeVestSpikes(
  bars: Iterable<VestBar>,
  timeframeMinutes: number,
  settings: SpikeSettings,
  nowMs: number,
  opts: VestRelVolOptions = {},
): VestSpike[] {
  const { threshold } = clampSpikeSettings(settings);
  return [...computeVestRelVols(bars, timeframeMinutes, settings, nowMs, opts).values()].filter((s) => s.relVol >= threshold);
}

// ---------------------------------------------------------------- placement

export type MarkerSide = 'above' | 'below';

/** Pixels other markers already use beyond a candle's high (above) / low (below). */
export type Occupancy = Map<string, number>;

export const occupancyKey = (timeSec: number, side: MarkerSide): string => `${timeSec}:${side}`;

/** Rough heights of the other candle markers, px (they stack outward too). */
export const MARKER_HEIGHTS = {
  /** Native series markers (BOS/CHoCH arrows, ⚡ whole-candle signals) incl. their text. */
  native: 22,
  /** 🔥 / 🧊 volume emoji. */
  volumeEmoji: 20,
  /** Skipped / Donchian / trailing-pullback triangles with a short tag. */
  small: 16,
  /** One line of the signal text stack (confidence, confluence, ML, P&L). */
  signalLine: 12,
} as const;

export interface OccupancyInput {
  /** Native markers: time in seconds, position aboveBar / belowBar / inBar. */
  native?: readonly { time: unknown; position?: string }[];
  /** Primitive markers with ms time and a side; `lines` for text stacks. */
  primitives?: readonly { timeMs: number; below: boolean; px: number }[];
}

export function buildOccupancy(input: OccupancyInput, intervalMs: number): Occupancy {
  const occ: Occupancy = new Map();
  const add = (timeSec: number, side: MarkerSide, px: number) => {
    const k = occupancyKey(timeSec, side);
    occ.set(k, (occ.get(k) ?? 0) + px);
  };
  for (const m of input.native ?? []) {
    if (typeof m.time !== 'number') continue;
    if (m.position === 'aboveBar') add(m.time, 'above', MARKER_HEIGHTS.native);
    else if (m.position === 'belowBar') add(m.time, 'below', MARKER_HEIGHTS.native);
  }
  const step = Math.max(60_000, intervalMs);
  for (const m of input.primitives ?? []) {
    const t = Math.floor((Math.floor(m.timeMs / step) * step) / 1000);
    add(t, m.below ? 'below' : 'above', m.px);
  }
  return occ;
}

/** Height of a signal-text stack for the fields it shows. */
export function signalStackPx(m: { confidence?: number; confluence?: number; mlScore?: number; isProfitable?: boolean; pnl?: number }): number {
  let lines = 0;
  if (m.confidence !== undefined) lines++;
  if (m.confluence !== undefined) lines++;
  if (m.mlScore !== undefined) lines++;
  if (m.isProfitable !== undefined || m.pnl !== undefined) lines++;
  return 6 + Math.max(1, lines) * MARKER_HEIGHTS.signalLine;
}

const fmt = (v: number): string => (Math.abs(v) >= 100 ? Math.round(v).toLocaleString('en-US') : v.toFixed(2));

/** Tooltip for one spike. */
export function vestSpikeTooltip(s: VestSpike, nqPerUnit = 1 / 20): { title: string; lines: string[] } {
  const lines = [
    `Vest NQ-PERP vol ${fmt(s.v)} vs avg ${fmt(s.avg)} (${basisDetail(s)}) (~${fmt(s.v * nqPerUnit)} NQ)`,
  ];
  if (s.buy !== null && s.sell !== null && s.delta !== null) {
    lines.push(`Taker buy ${fmt(s.buy)} · sell ${fmt(s.sell)} · delta ${s.delta >= 0 ? '+' : ''}${fmt(s.delta)}`);
  } else {
    lines.push('Kline-derived bar: no buy/sell split');
  }
  lines.push('Closed bars only. Descriptive, not a signal.');
  return { title: `🐋 Vest vol ${s.relVol.toFixed(1)}x`, lines };
}
